import { assertScrubbed } from './scrubPII';

export function runInsightEngine(validatedData) {
  assertScrubbed(validatedData, 'runInsightEngine');
  const d = validatedData;

  const archetype  = detectArchetype(d);
  const bottleneck = diagnoseBottleneck(d);
  const forecast   = buildForecast(d);
  const actions    = buildPrecisionActions(d, bottleneck, forecast);
  const signals    = extractPerformanceSignals(d);

  return { archetype, bottleneck, forecast, actions, signals, dataQuality: d.dataQuality };
}

export const ARCHETYPES = {
  PACE_SETTER:           { id: 'PACE_SETTER',           label: 'The Pace Setter',           summary: 'On track with disciplined, consistent output. Focus shifts to protecting pace and compounding gross quality.' },
  VOLUME_GRINDER:        { id: 'VOLUME_GRINDER',        label: 'The Volume Grinder',        summary: 'Strong outreach effort — but conversion is the gap. Activity is not the constraint. Precision is.' },
  LATE_STAGE_CLOSER:     { id: 'LATE_STAGE_CLOSER',     label: 'The Late-Stage Closer',     summary: 'Performance concentrates at month-end, creating income instability and unnecessary end-of-month risk.' },
  INCONSISTENT_SPRINTER: { id: 'INCONSISTENT_SPRINTER', label: 'The Inconsistent Sprinter', summary: 'Strong output in bursts, separated by flat days. Compounding inconsistency is the primary limiter.' },
  GROSS_HUNTER:          { id: 'GROSS_HUNTER',          label: 'The Gross Hunter',          summary: 'Quality deals — but volume may be leaving commission on the table.' },
  APPOINTMENT_SPECIALIST:{ id: 'APPOINTMENT_SPECIALIST',label: 'The Appointment Specialist',summary: 'Strong in the room. Top-of-funnel generation is the bottleneck.' },
  OPPORTUNITY_LEAKER:    { id: 'OPPORTUNITY_LEAKER',    label: 'The Opportunity Leaker',    summary: 'Contact volume is sufficient — but follow-up sequencing is bleeding out leads.' },
  DEVELOPING:            { id: 'DEVELOPING',            label: 'Building Momentum',         summary: 'Limited data. Patterns will sharpen as activity accumulates.' },
};

function detectArchetype(d) {
  if (d.paceToGoal >= 90 && d.unitsSold >= d.goal * 0.7)
    return ARCHETYPES.PACE_SETTER;
  if (d.totalOutreach > 80 && d.leadConversionRate != null && d.leadConversionRate < 15)
    return ARCHETYPES.VOLUME_GRINDER;
  if (d.unitsLastWeek != null && d.unitsSold > 0 && (d.unitsLastWeek / d.unitsSold) > 0.45)
    return ARCHETYPES.LATE_STAGE_CLOSER;
  if (d.zeroDays > d.sellingDaysElapsed * 0.3)
    return ARCHETYPES.INCONSISTENT_SPRINTER;
  if (d.avgGrossPerUnit > 3500 && d.unitsSold < d.goal * 0.5)
    return ARCHETYPES.GROSS_HUNTER;
  if (d.apptShowRate != null && d.apptShowRate > 65 && d.apptSetRate != null && d.apptSetRate < 20)
    return ARCHETYPES.APPOINTMENT_SPECIALIST;
  if (d.leadsContacted > 40 && d.leadConversionRate != null && d.leadConversionRate < 10)
    return ARCHETYPES.OPPORTUNITY_LEAKER;
  return ARCHETYPES.DEVELOPING;
}

function diagnoseBottleneck(d) {
  const candidates = [
    {
      id: 'LEAD_STARVATION',
      condition: d.leadsContacted < 20 && d.paceToGoal < 60,
      title: 'Insufficient Top-of-Funnel Volume',
      diagnosis: `Only ${d.leadsContacted} contacts logged through ${d.sellingDaysElapsed} selling days. At current conversion rates, the pipeline lacks the volume needed for goal attainment without a significant increase in new contacts.`,
      lever: 'Increase daily new contacts by 3–5 to build sustainable pipeline pressure through month-end.',
    },
    {
      id: 'FOLLOW_UP_DECAY',
      condition: d.firstTouchRate != null && d.secondTouchRate != null && d.firstTouchRate > 60 && d.secondTouchRate < 35,
      title: 'Follow-Up Sequencing Breakdown',
      diagnosis: `First-touch contact rate is solid — but only ${Math.round(d.secondTouchRate ?? 0)}% of leads receive a second touch. The majority of eventual sales require 3+ contacts. The pipeline is leaking at the second engagement.`,
      lever: 'Before prospecting new leads today, re-engage every lead from the past 72 hours without a follow-up.',
    },
    {
      id: 'APPOINTMENT_SHOW_LEAKAGE',
      condition: d.apptSetRate != null && d.apptShowRate != null && d.apptSetRate > 20 && d.apptShowRate < 50,
      title: 'Appointment Show Rate Leakage',
      diagnosis: `Appointments are being set (${Math.round(d.apptSetRate ?? 0)}% set rate) but only ${Math.round(d.apptShowRate ?? 0)}% show. That gap represents real floor traffic — and real commission — lost between the calendar and the lot.`,
      lever: 'Implement a 2-step confirmation: text the evening before, call 2 hours prior to every appointment.',
    },
    {
      id: 'CLOSE_EFFICIENCY',
      condition: d.apptShowRate != null && d.apptCloseRate != null && d.apptShowRate > 55 && d.apptCloseRate < 35,
      title: 'Closing Efficiency Gap',
      diagnosis: `Show rate is solid at ${Math.round(d.apptShowRate ?? 0)}% — but close rate sits at ${Math.round(d.apptCloseRate ?? 0)}%. The constraint is in the desk conversation, not the pipeline.`,
      lever: 'Review the last 3–5 non-closes. Identify the recurring objection pattern. Address it directly.',
    },
    {
      id: 'VOLUME_PRECISION_IMBALANCE',
      condition: d.totalOutreach > 100 && d.leadConversionRate != null && d.leadConversionRate < 8,
      title: 'High Volume, Low Precision',
      diagnosis: `${d.totalOutreach} total outreach activities at a ${Math.round(d.leadConversionRate ?? 0)}% conversion rate. High outreach with low conversion points to qualification gaps — not effort gaps.`,
      lever: 'Narrow focus to the 30–40 highest-signal prospects. Depth of engagement beats breadth.',
    },
    {
      id: 'END_OF_MONTH_COMPRESSION',
      condition: d.unitsLastWeek != null && d.unitsSold > 0 && (d.unitsLastWeek / d.unitsSold) > 0.5 && d.sellingDaysRemaining < 10,
      title: 'End-of-Month Compression Risk',
      diagnosis: `Over half of this month's units closed in the final week. This pattern creates income volatility and typically results in weaker gross as deals are rushed through at month-end.`,
      lever: 'Next month: set a daily contact floor for weeks 1–3 rather than relying on a final-week sprint.',
    },
  ];

  return candidates.find(c => c.condition) ?? {
    id: 'BALANCED',
    title: 'No Single Dominant Constraint',
    diagnosis: 'Metrics show broadly balanced performance. Focus shifts to protecting pace and compounding gross quality.',
    lever: 'Maintain activity floor. Introduce gross-focused negotiation discipline on every deal.',
  };
}

function buildForecast(d) {
  const daysLeft    = d.sellingDaysRemaining;
  const pace        = d.currentPacePerDay;
  const commPerUnit = d.unitsSold > 0 && d.commissionMTD > 0
                      ? d.commissionMTD / d.unitsSold : 1200;
  const unitsNeeded = Math.max(0, d.goal - d.unitsSold);
  const dailyNeeded = daysLeft > 0 ? parseFloat((unitsNeeded / daysLeft).toFixed(1)) : null;
  const baseline    = Math.round(d.unitsSold + pace * daysLeft);
  const optimistic  = Math.round(d.unitsSold + pace * daysLeft * 1.25);

  return {
    baseline:  { units: baseline,   commission: Math.round(baseline  * commPerUnit), vsGoal: baseline  - d.goal, label: 'Current pace holds' },
    optimistic:{ units: optimistic, commission: Math.round(optimistic * commPerUnit), vsGoal: optimistic - d.goal, label: 'Activity up 25%' },
    goal:      { units: d.goal,     commission: Math.round(d.goal    * commPerUnit), vsGoal: 0, label: 'Goal attained', dailyUnitsNeeded: dailyNeeded },
    onTrack:         baseline >= d.goal * 0.9,
    compressionRisk: d.unitsLastWeek != null && d.unitsSold > 0 && (d.unitsLastWeek / d.unitsSold) > 0.45,
    endOfMonthRisk:  baseline < d.goal * 0.75 && daysLeft < 8,
  };
}

function buildPrecisionActions(d, bottleneck, forecast) {
  const actions  = [];
  const daysLeft = d.sellingDaysRemaining;

  if (daysLeft > 2 && forecast.goal.dailyUnitsNeeded != null) {
    const floor = Math.ceil(forecast.goal.dailyUnitsNeeded * 8);
    actions.push({ priority: 1, category: 'PACE',
      text: `${d.goal - d.unitsSold} units needed across ${daysLeft} selling days — ${forecast.goal.dailyUnitsNeeded} per day. Set a non-negotiable daily floor of ${floor} meaningful outreach contacts.` });
  }
  if (d.secondTouchRate != null && d.secondTouchRate < 40) {
    actions.push({ priority: 2, category: 'FOLLOW_UP',
      text: `Only ${Math.round(d.secondTouchRate)}% of contacted leads have received a second touch. Before any new cold outreach today, re-engage every lead from the past 72 hours. Second touches close deals; first touches start conversations.` });
  }
  if (d.apptShowRate != null && d.apptShowRate < 55 && d.apptSet > 3) {
    const lostAppts = Math.round((1 - d.apptShowRate / 100) * d.apptSet);
    actions.push({ priority: 3, category: 'APPOINTMENTS',
      text: `~${lostAppts} set appointments are not converting to floor visits this month. Add a two-step confirmation — evening-before text and 2-hour-prior call. This typically recovers 15–20 points of show rate.` });
  }
  if (forecast.compressionRisk) {
    const floor = Math.ceil((forecast.goal.dailyUnitsNeeded ?? 1) * 6);
    actions.push({ priority: 4, category: 'DISTRIBUTION',
      text: `Over 45% of units came from the final 7 days — back-loading creates income volatility and weaker gross. Build a daily minimum of ${floor} contacts through weeks 1–3 of every month.` });
  }
  if (d.avgGrossPerUnit > 0 && d.avgGrossPerUnit < 2500 && d.unitsSold > 2) {
    const grossGap = (2800 - d.avgGrossPerUnit) * d.unitsSold;
    actions.push({ priority: 5, category: 'GROSS',
      text: `Average gross of ${d.avgGrossPerUnit.toLocaleString()} is ~${(2800 - d.avgGrossPerUnit).toLocaleString()} below benchmark. Across ${d.unitsSold} units that is approximately ${grossGap.toLocaleString()} in reachable earnings. Hold longer before moving to first pencil.` });
  }
  return actions.slice(0, 4);
}

function extractPerformanceSignals(d) {
  return {
    isActivityHeavy:      d.totalOutreach > 80,
    isConversionLight:    d.leadConversionRate != null && d.leadConversionRate < 15,
    hasStrongGross:       d.avgGrossPerUnit > 3000,
    hasConsistencyIssue:  d.zeroDays > d.sellingDaysElapsed * 0.3,
    isBackLoaded:         d.unitsLastWeek != null && d.unitsSold > 0 && (d.unitsLastWeek / d.unitsSold) > 0.4,
    hasFollowUpGap:       d.secondTouchRate != null && d.secondTouchRate < 35,
    hasApptLeverage:      d.apptShowRate != null && d.apptShowRate > 65,
    isOnTrack:            d.paceToGoal >= 90,
  };
}
